import R from 'ramda'
import { encryptWithSymmetricEncryption, encryptWithAsymmetricEncryption, decryptWithSymmetricEncryption, decryptWithAsymmetricEncryption } from './key'
import { createHeader, createFilesEncryptionParams, createFilesStorageParams } from './format'

/**
 * Prepare arguments for multiple symmetric encryptions
 *
 * @param {Base64UrlString} encryptionKey
 * @param {{derivationParams: DerivationParams, encryptionParams: SymmetricEncryptionParams, password: String, hint: String}[]} symmetricEncryptionParams
 * @return {Array.<Array.<Base64UrlString, DerivationParams, SymmetricEncryptionParams, String, String>>} 2d array of arguments compatible with encryptKeyWithSymmetricEncryption
 * @private
 */
export function _argumentsForMultipleSymmetricKeyEncryptions (encryptionKey, symmetricEncryptionParams) {
  return R.map(
    R.compose(
      R.prepend(encryptionKey),
      R.props(['derivationParams', 'encryptionParams', 'password', 'hint'])
    )
  )(symmetricEncryptionParams)
}

/**
 * Prepare arguments for multiple asymmetric encryptions
 *
 * @param {Base64UrlString} encryptionKey
 * @param {{tag: Base64UrlString, key: Base64UrlString, encryptionParams: SymmetricEncryptionParams, id: String, version: String}[]} asymmetricEncryptionParams
 * @return {Array.<Array.<Base64UrlString, Base64UrlString, SymmetricEncryptionParams, Base64UrlString, String, String>>} 2d array of arguments compatible with encryptKeyWithAsymmetricEncryption
 * @private
 */
export function _argumentsForMultipleAsymmetricKeyEncryptions (encryptionKey, asymmetricEncryptionParams) {
  return R.map(
    R.compose(
      R.prepend(encryptionKey),
      R.props(['tag', 'encryptionParams', 'key', 'id', 'version'])
    )
  )(asymmetricEncryptionParams)
}

/**
 * Prepare arguments for multiple asymmetric encryptions
 *
 * @param {{name: String, size: Number, type: String, data: ArrayBuffer, id: String, path: String, service: String, encryptionParams: SymmetricEncryptionParams}[]} files
 * @return {FilesEncryptionParams[]}
 * @private
 */
export function _multipleFilesEncryptionParams (files) {
  return R.map(
    R.compose(
      R.apply(createFilesEncryptionParams),
      R.props(['id', 'encryptionParams'])
    )
  )(files)
}

/**
 * Prepare arguments for multiple files storage
 *
 * @param {{name: String, size: Number, type: String, data: ArrayBuffer, id: String, path: String, service: String, encryptionParams: SymmetricEncryptionParams}[]} files
 * @return {FilesStorageParams[]}
 * @private
 */
export function _multipleFilesStorageParams (files) {
  return R.map(
    R.compose(
      R.apply(createFilesStorageParams),
      R.props(['id', 'path', 'service'])
    )
  )(files)
}

/**
 * Encrypt key with both symmetric encryption and asymmetric encryption
 *
 * @param {Base64UrlString} encryptionKey
 * @param {SymmetricEncryptionParams} encryptionParams
 * @param {{name: String, size: Number, type: String, data: ArrayBuffer, id: String, path: String, service: String, encryptionParams: SymmetricEncryptionParams}[]} files
 * @param {{derivationParams: DerivationParams, encryptionParams: SymmetricEncryptionParams, password: String, hint: String}[]} symmetricEncryptionParams
 * @param {{tag: Base64UrlString, key: Base64UrlString, encryptionParams: SymmetricEncryptionParams, id: String, version: String}[]} asymmetricEncryptionParams
 * @return {Header}
 * @throws {Error} if a parameter is invalid
 * @private
 */
export function encryptKey (encryptionKey, encryptionParams, files, symmetricEncryptionParams, asymmetricEncryptionParams) {
  return R.useWith(
    createHeader,
    [
      R.compose(R.map(R.apply(encryptWithSymmetricEncryption)), R.apply(_argumentsForMultipleSymmetricKeyEncryptions)),
      R.compose(R.map(R.apply(encryptWithAsymmetricEncryption)), R.apply(_argumentsForMultipleAsymmetricKeyEncryptions)),
      R.identity,
      _multipleFilesEncryptionParams,
      _multipleFilesStorageParams
    ]
  )([encryptionKey, symmetricEncryptionParams], [encryptionKey, asymmetricEncryptionParams], encryptionParams, files, files)
}

/**
 * Decrypt key with symmetric encryption
 *
 * @param {String} password
 * @param {Header} header
 * @return {Base64UrlString} encrypted key
 * @throws {Error} if the symmetricEncryption array is empty, data is corrupt or wrong password
 * @public
 * @example
 *  const keys = CryptolizeCore.createAsymmetricKeys()
 *  const password = 'password'
 *  const hint = 'hint'
 *  const record = CryptolizeCore.encryptRecord(..., keys.public, ..., [{ password, hint }], ...)
 *  const headers = CryptolizeCore.decryptRecord(record, keys.private)
 *  const key = CryptolizeCore.decryptKeyWithSymmetricEncryption(password, header[0])
 *  console.log(key)
 */
export function decryptKeyWithSymmetricEncryption (password, { symmetricEncryption }) {
  return R.ifElse(
    R.isNil,
    () => { throw new Error('symmetricEncryption array is empty or wrong password') },
    R.tryCatch(
      R.curry(decryptWithSymmetricEncryption)(password),
      () => decryptKeyWithSymmetricEncryption(password, { symmetricEncryption: R.tail(symmetricEncryption) })
    )
  )(R.head(symmetricEncryption))
}

/**
 * Decrypt key with asymmetric encryption
 *
 * @param {String} id
 * @param {String} version
 * @param {ElGamalPrivateKey} privateKey
 * @param {Header} header
 * @return {Base64UrlString} encrypted key
 * @throws {Error} if the asymmetricEncryption array is empty, data is corrupt or wrong keyId
 * @public
 * @example
 *  const headerKeys = CryptolizeCore.createAsymmetricKeys()
 *  const id = 'id'
 *  const keys = CryptolizeCore.createAsymmetricKeys()
 *  const password = 'password'
 *  const hint = 'hint'
 *  const record = CryptolizeCore.encryptRecord(..., headerKeys.public, ..., [{ id: id, version: keys.public, key: keys.public }], ...)
 *  const headers = CryptolizeCore.decryptRecord(record, headerKeys.private)
 *  const key = CryptolizeCore.decryptKeyWithAsymmetricEncryption(id, keys.public, keys.private, header[0])
 *  console.log(key)
 */
export function decryptKeyWithAsymmetricEncryption (id, version, privateKey, { asymmetricEncryption }) {
  return R.compose(
    R.ifElse(
      R.isNil,
      () => { throw new Error('asymmetricEncryption array is empty or wrong keyId') },
      R.curry(decryptWithAsymmetricEncryption)(privateKey)
    ),
    R.find(R.converge(R.and, [ R.propEq('id', id), R.propEq('version', version) ]))
  )(asymmetricEncryption)
}

/**
 * returns true if symmetric encryption exists, false otherwise
 *
 * @param {Header} header
 * @return {Boolean}
 * @public
 * @example
 *  const headerKeys = CryptolizeCore.createAsymmetricKeys()
 *  const id = 'id'
 *  const keys = CryptolizeCore.createAsymmetricKeys()
 *  const password = 'password'
 *  const hint = 'hint'
 *  const record = CryptolizeCore.encryptRecord(..., headerKeys.public, ..., [{ id: id, version: keys.public, key: keys.public }], ...)
 *  const headers = CryptolizeCore.decryptRecord(record, headerKeys.private)
 *  const hasSymmetricEncryption = CryptolizeCore.hasSymmetricEncryption(header[0])
 *  console.log(hasSymmetricEncryption)
 */
export function hasSymmetricEncryption ({ symmetricEncryption }) {
  return symmetricEncryption.length > 0
}

/**
 * returns true if asymmetric encryption with supplied id exists, false otherwise
 *
 * @param {String} id
 * @param {Header} header
 * @return {Boolean}
 * @public
 * @example
 *  const headerKeys = CryptolizeCore.createAsymmetricKeys()
 *  const id = 'id'
 *  const keys = CryptolizeCore.createAsymmetricKeys()
 *  const password = 'password'
 *  const hint = 'hint'
 *  const record = CryptolizeCore.encryptRecord(..., headerKeys.public, ..., [{ id: id, version: keys.public, key: keys.public }], ...)
 *  const headers = CryptolizeCore.decryptRecord(record, headerKeys.private)
 *  const hasAsymmetricEncryption = CryptolizeCore.hasAsymmetricEncryption(id, header[0])
 *  console.log(hasAsymmetricEncryption)
 */
export function hasAsymmetricEncryption (id, header) {
  return R.not(R.isNil(getAsymmetricEncryptionParams(id, header)))
}

/**
 * returns asymmetric encryption params of the supplied id
 *
 * @param {String} id
 * @param {Header} header
 * @return {(AsymmetricEncryptionWrapper|undefined)}
 * @public
 * @example
 *  const headerKeys = CryptolizeCore.createAsymmetricKeys()
 *  const id = 'id'
 *  const keys = CryptolizeCore.createAsymmetricKeys()
 *  const password = 'password'
 *  const hint = 'hint'
 *  const record = CryptolizeCore.encryptRecord(..., headerKeys.public, ..., [{ id: id, version: keys.public, key: keys.public }], ...)
 *  const headers = CryptolizeCore.decryptRecord(record, headerKeys.private)
 *  const asymmetricEncryptionParams = CryptolizeCore.getAsymmetricEncryptionParams(id, header[0])
 *  console.log(asymmetricEncryptionParams)
 */
export function getAsymmetricEncryptionParams (id, { asymmetricEncryption }) {
  return R.find(R.propEq('id', id), asymmetricEncryption)
}
